// Matches --motion-slow (src/styles/tokens.css) — the total-points card's
// fade+scale entrance. The count-up is timed to start right as
// that transition finishes, so this must stay in sync with the CSS duration
// on .profile__score-wrap.
export const SCORE_FADE_IN_MS = 320
// Within the ~0.8-1.2s range asked for; no matching token exists yet since
// nothing else in the app runs a count-up.
export const SCORE_COUNT_UP_MS = 1000

// Module-scoped (not component state, not sessionStorage/localStorage): reset
// only when the JS runtime restarts (full reload, or the PWA fully closed and
// relaunched), which is what "once per fresh app load" means here. Profile
// unmounts/remounts on every tab switch (App.tsx, `activeTab === 'home'`), so
// state stored on the component itself would replay the animation on every
// homepage visit — this flag is what makes it survive that.
let hasPlayedScoreCountUp = false

export function hasScoreCountUpPlayed(): boolean {
  return hasPlayedScoreCountUp
}

export function markScoreCountUpPlayed(): void {
  hasPlayedScoreCountUp = true
}

/** Test-only seam: renderToStaticMarkup never runs effects, so nothing in the
 * test environment can flip the flag the way a real mount does. Lets tests
 * simulate "this load already played the count-up." */
export function __setHasPlayedScoreCountUpForTests(played: boolean): void {
  hasPlayedScoreCountUp = played
}

// Same "once per fresh app load" idea as hasPlayedScoreCountUp, for the GW
// Points hero. Kept as its own flag since it gates a different
// value with its own mount lifecycle (GameweekGrid, not Profile) — reusing
// the score flag would either skip this animation or replay that one
// incorrectly depending on load order.
let hasPlayedGwPointsCountUp = false

export function hasGwPointsCountUpPlayed(): boolean {
  return hasPlayedGwPointsCountUp
}

export function markGwPointsCountUpPlayed(): void {
  hasPlayedGwPointsCountUp = true
}

export function __setHasPlayedGwPointsCountUpForTests(played: boolean): void {
  hasPlayedGwPointsCountUp = played
}

// Accessed via `globalThis` (structurally typed, not the ambient DOM `Window`)
// so this file type-checks under tsconfig.server.json too — it's pulled into
// that program's compilation unit because scoreCountUp.test.ts (a `*.test.ts`
// file included there) imports it, and that project's lib list is DOM-free.
interface MatchMediaGlobal {
  window?: {
    matchMedia?: (query: string) => { matches: boolean }
  }
}

export function prefersReducedMotion(): boolean {
  const matchMedia = (globalThis as MatchMediaGlobal).window?.matchMedia
  return (
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function easeOutCubic(progress: number): number {
  return 1 - Math.pow(1 - progress, 3)
}
